import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Form } from '@qodo/design-system';
import { zodResolver } from '@hookform/resolvers/zod';
import { useConfigureGitlabMultiMutation } from 'network/mutations/useConfigureGitlabMultiMutation';
import { useConfigureGitlabMutation } from 'network/mutations/useConfigureGitlabMutation';
import { ROUTES } from 'routes/consts';
import { Step } from 'shared/InstallationDialog';
import { useMultiStepDialog } from 'shared/MultiStepDialog';
import { cn } from 'utils/className';
import { spawn } from 'utils/translations';
import { prependHttpsIfNeeded } from '../../GithubManifestInstallationDialog/utils';
import { CONFIGURE_APP_FIELDS, FORM_IDS } from '../consts';
import { useGitlabInstallation } from '../gitlabInstallationContext';
import { getConfigureAppFormSchema } from '../schemas';
import { ConfigureAppFormData } from '../types';
import { splitGitlabUrlLines } from '../utils';

export const ConfigureApp = () => {
  const { setEarliestStepAvailable, setIsPending, goNext } =
    useMultiStepDialog();
  const { setIsGitlabAppConfigured: setIsConfigured } = useGitlabInstallation();

  const methods = useForm<ConfigureAppFormData>({
    resolver: zodResolver(getConfigureAppFormSchema()),
    defaultValues: {
      [CONFIGURE_APP_FIELDS.CLIENT_ID]: '',
      [CONFIGURE_APP_FIELDS.CLIENT_SECRET]: '',
      [CONFIGURE_APP_FIELDS.GITLAB_URL]: '',
    },
  });

  const { mutate: configureGitlab, isPending: isConfigureGitlabPending } =
    useConfigureGitlabMutation();
  const { mutate: configureGitlabMulti, isPending: isConfigureMultiPending } =
    useConfigureGitlabMultiMutation();
  const isConfigurePending =
    isConfigureGitlabPending || isConfigureMultiPending;

  const spawnConfigure = spawn('dialogs.gitlab-installation.configure-app');

  const formClassName = cn(
    "[&_[data-slot='form-label']]:font-mono",
    "[&_[data-slot='form-label']]:text-xs",
    "[&_[data-slot='form-label']]:leading-[140%]",
    "[&_[data-slot='form-description']]:text-text-bg-neutral-mild",
  );

  useEffect(() => {
    setEarliestStepAvailable(0);
  }, []);

  useEffect(() => {
    setIsPending(isConfigurePending);
  }, [isConfigurePending]);

  const onSubmit = (data: ConfigureAppFormData) => {
    setIsPending(true);
    const frontendRedirectUrl = `${window.location.origin}${ROUTES.GITLAB_INSTALLATION_STEP_5}${location.search}`;
    const urls = splitGitlabUrlLines(data.gitlabUrl).map(prependHttpsIfNeeded);

    const mutationOptions = {
      onSuccess: () => {
        setIsConfigured(true);
        goNext();
      },
      onError: () => {
        setIsPending(false);
      },
    };

    // More than one pasted URL → multi-group flow (B-v1a). The OAuth callback
    // stashes a provision session and redirects with status=awaiting_provision.
    if (urls.length > 1) {
      configureGitlabMulti(
        {
          clientId: data.clientId,
          clientSecret: data.clientSecret,
          gitlabUrls: urls,
          frontendRedirectUrl,
        },
        mutationOptions,
      );

      return;
    }

    configureGitlab(
      {
        clientId: data.clientId,
        clientSecret: data.clientSecret,
        gitlabUrl: urls[0],
        frontendRedirectUrl,
      },
      mutationOptions,
    );
  };

  return (
    <Step
      title={spawnConfigure('title')}
      description={spawnConfigure('description')}
    >
      <Form
        methods={methods}
        onSubmit={onSubmit}
        className={formClassName}
        {...({ id: FORM_IDS.CONFIGURE_APP } as Record<string, string>)}
      >
        <Form.Textarea
          name={CONFIGURE_APP_FIELDS.GITLAB_URL}
          label={spawnConfigure('gitlab-url.label')}
          placeholder={spawnConfigure('gitlab-url.placeholder')}
          description={spawnConfigure('gitlab-url.description')}
        />
        <Form.Input
          name={CONFIGURE_APP_FIELDS.CLIENT_ID}
          label={spawnConfigure('client-id.label')}
          placeholder={spawnConfigure('client-id.placeholder')}
        />
        <Form.Input
          type="password"
          name={CONFIGURE_APP_FIELDS.CLIENT_SECRET}
          label={spawnConfigure('client-secret.label')}
          placeholder={spawnConfigure('client-secret.placeholder')}
        />
      </Form>
    </Step>
  );
};
